/**
 * What the flight model asks of an engine, whatever kind of engine it is.
 *
 * ## Why this interface exists
 *
 * Non-negotiable #8: the core FDM contains zero aircraft-specific branches. Until M6 that was
 * easy to honour because every aeroplane in the project had a piston engine and a propeller, so
 * `fdm.ts` could name `Propulsion` directly and still be telling the truth. Adding a turbofan is
 * the first thing that could have broken it — and the tempting shape, a `switch` on
 * `config.category` inside the tick, would have broken it permanently and quietly.
 *
 * So the FDM talks to a {@link Powerplant} and the config decides which one it gets. A jet is not
 * a special case in the flight model; it is a different implementation of the same six methods.
 *
 * ## Why the whole atmosphere sample, rather than density
 *
 * A piston engine only needs density: its power lapses with σ and the propeller's coefficients are
 * non-dimensional. A turbofan needs more than that — thrust lapses with pressure ratio and falls
 * off with flight Mach, and Mach needs the local speed of sound, which needs temperature. Density
 * alone cannot produce either, and deriving them by inverting the ISA would be reconstructing
 * information the caller already has in its hand.
 *
 * Both the FDM and the trim solver sample the atmosphere every tick anyway, so passing the sample
 * costs nothing and removes the temptation to smuggle Mach in through a mutable field.
 *
 * ## `rpm` means "how fast is it turning", loosely
 *
 * For a piston engine it is the crankshaft, in revolutions per minute, and it is a genuine state
 * integrated from a torque balance. For a turbofan it is the core spool as a percentage of
 * maximum — N2, the number on the gauge — carried in the same field because everything downstream
 * (the instruments, the audio, the trim solver's inner equilibrium) wants "the engine's speed" and
 * does not care about the units it happens to be quoted in. Each implementation documents its own.
 *
 * `updateFixed(dt)`: yes, at 120 Hz, in every implementation. Allocation-free after construction.
 */

import type { AtmosphereSample } from './atmosphere.js';

/** Everything a powerplant produces in one tick. */
export interface PropulsionOutput {
  /** Thrust along the body x axis [N]. */
  thrustN: number;
  /** Shaft torque absorbed [N·m]. Zero for a jet, which has no propeller to absorb any. */
  torqueNm: number;
  /** Roll moment on the airframe from torque reaction [N·m]. */
  rollMomentNm: number;
  /** Yaw moment from P-factor [N·m]. */
  yawMomentNm: number;
  /** Pitch moment from P-factor in sideslip [N·m]. */
  pitchMomentNm: number;
  /** Axial velocity increment inside the propeller slipstream [m/s]. Zero for a jet. */
  slipstreamAxialMps: number;
  /** Swirl as an increment to the aircraft's velocity relative to the local air, body y [m/s]. */
  slipstreamSwirlYMps: number;
  /** Current shaft speed — rpm for a piston, N2 percentage for a turbine. */
  rpm: number;
  /** Shaft power actually produced [W]. Zero for a jet, which does not produce shaft power. */
  powerW: number;
  /** Propulsive efficiency [-], zero when static. */
  efficiency: number;
  /**
   * How far into reheat the engine is, 0…1.
   *
   * Always zero for anything without an afterburner. It is in the shared output rather than on the
   * turbofan alone because the things that consume it — the flame, the heat shimmer, the reheat
   * roar in `audio/` — read the flight state, and a renderer that had to ask what kind of engine
   * it was looking at would be the same rule broken one level up.
   */
  afterburnerFraction: number;
  /** Fuel flow [kg/s]. Zero when the model does not track it. */
  fuelFlowKgPerS: number;
}

export function makePropulsionOutput(): PropulsionOutput {
  return {
    thrustN: 0,
    torqueNm: 0,
    rollMomentNm: 0,
    yawMomentNm: 0,
    pitchMomentNm: 0,
    slipstreamAxialMps: 0,
    slipstreamSwirlYMps: 0,
    rpm: 0,
    powerW: 0,
    efficiency: 0,
    afterburnerFraction: 0,
    fuelFlowKgPerS: 0,
  };
}

export interface Powerplant {
  /** Reused between ticks; never retained by callers. */
  readonly output: PropulsionOutput;

  /** Magnetos, or the engine master. False means it will not run and will not restart. */
  ignitionOn: boolean;
  /** Starter motor, held. */
  starterEngaged: boolean;
  /** Is the engine actually producing power? */
  readonly running: boolean;
  /** Shaft speed. See the note in this file's header on what the units are. */
  rpm: number;

  /**
   * Advance one tick.
   *
   * @param throttle        Lever position, 0…1. What that means at the top of the range is the
   *                        implementation's business — on the fighter the last tenth is reheat.
   * @param axialSpeedMps   Airspeed component along the body x axis at the engine [m/s].
   * @param alphaRad        Angle of attack at the intake or propeller disc [rad].
   * @param betaRad         Sideslip there [rad].
   * @param air             The atmosphere at the aircraft.
   * @param dtSeconds       Fixed timestep [s].
   */
  updateFixed(
    throttle: number,
    axialSpeedMps: number,
    alphaRad: number,
    betaRad: number,
    air: AtmosphereSample,
    dtSeconds: number,
  ): PropulsionOutput;

  /**
   * The steady shaft speed for this throttle and condition.
   *
   * The trim solver needs an equilibrium rather than a transient: it evaluates the aircraft
   * thousands of times looking for a root, and an engine still spooling would make every
   * evaluation a slightly different aeroplane. For a piston this is a genuine torque balance
   * solved by bisection; for a turbofan it is simply where the spool would settle, because a
   * turbofan's steady state is commanded rather than balanced.
   */
  equilibriumRpm(throttle: number, axialSpeedMps: number, air: AtmosphereSample): number;

  /** Put the engine to a running state at this speed, without a start sequence. */
  forceRunning(rpm: number): void;
  /** Cut the ignition and let it wind down. */
  shutDown(): void;
  /** Mags on, starter held. */
  engageStarter(): void;
  releaseStarter(): void;
}
